import type { ReactNode } from 'react';
import type { PricingBasis } from '@ace-tracker/shared';

/**
 * What an indicative cost figure is and isn't (E1.11): the cost basis, that it
 * is not an invoice, when prices were last reviewed, and how much of it used
 * the fallback rate. `children` adds view-specific caveats.
 */
export function PricingBasisNote({
  basis,
  totalCost,
  unpricedModels,
  children,
}: {
  basis: Pick<PricingBasis, 'source' | 'reviewedAt' | 'stale'> & { fallbackCost?: number };
  totalCost?: number;
  unpricedModels: string[];
  children?: ReactNode;
}) {
  const fallbackShare =
    basis.fallbackCost !== undefined && totalCost !== undefined && totalCost > 0 ? (basis.fallbackCost / totalCost) * 100 : null;
  return (
    <div className="space-y-1 text-xs text-text-muted">
      <p>
        <span className="font-medium text-text-default">Indicative cost basis:</span> {basis.source}. Tokens × the prices in Settings →
        Model pricing, each day at the price in force that day. Not an invoice: excludes plan and seat charges, bundled usage,
        discounts and taxes. {children}
      </p>
      <p>
        {basis.stale && <i aria-hidden="true" className="fa-solid fa-triangle-exclamation mr-1.5 text-warn-ink" />}
        Prices last reviewed {new Date(basis.reviewedAt).toLocaleDateString()}
        {basis.stale && ' — over 90 days ago, so they may be out of date'}.
        {unpricedModels.length > 0 && (
          <>
            {' '}
            {unpricedModels.length} model{unpricedModels.length === 1 ? '' : 's'} used the fallback rate ({unpricedModels.join(', ')})
            {fallbackShare !== null && <>, {fallbackShare < 0.1 ? '<0.1' : fallbackShare.toFixed(1)}% of this cost</>} — add pricing in
            Settings for an accurate figure.
          </>
        )}
      </p>
    </div>
  );
}
